import type { SectionId, SegmentInfo } from '../../../features/engagement/domain/models/sections'
import { ChartColors } from '../../shared/charts'

export type TranslateFn = (key: string) => string

export const SECTION_LABEL_KEYS: Record<SectionId, string> = {
  home: 'ls_sections_section_home',
  community: 'ls_sections_section_community',
  chat: 'ls_sections_section_chat',
  songs: 'ls_sections_section_songs',
  videos: 'ls_sections_section_videos',
  events: 'ls_sections_section_events',
  livestream: 'ls_sections_section_livestream',
  shop: 'ls_sections_section_shop',
  other: 'ls_sections_section_other'
}

export const SECTION_COLORS: Record<SectionId, string> = {
  home: ChartColors.blueBright,
  community: ChartColors.orange,
  chat: ChartColors.purpleLight,
  songs: ChartColors.green,
  videos: ChartColors.cyan,
  events: ChartColors.yellow,
  livestream: ChartColors.red,
  shop: ChartColors.indigo,
  other: ChartColors.gray
}

// Tier palette for the per-user comparison: paid tiers by API order (top tier gold,
// second bronze, remaining paid tiers blue shades), free is always gray. Gold and bronze
// are separated on lightness, not just hue, so they stay distinct as small legend dots.
const SEGMENT_PAID_COLORS = [ChartColors.amber, ChartColors.bronze, ChartColors.blueBright, ChartColors.blue] as const

export const getSegmentColors = (segments: SegmentInfo[]): Record<string, string> => {
  const colors: Record<string, string> = {}
  let paidIndex = 0

  for (const segment of segments) {
    colors[segment.key] =
      segment.kind === 'free' ? ChartColors.gray : SEGMENT_PAID_COLORS[paidIndex++ % SEGMENT_PAID_COLORS.length]
  }

  return colors
}

/**
 * Formats a duration in seconds as "Xh Ym" (e.g. 5400 -> "1h 30m", 38_988_720 -> "10,830h 12m").
 * Negative or non-finite inputs render as "0h 0m".
 */
export const formatDurationHours = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  const totalMinutes = Math.round(safeSeconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours.toLocaleString('en-US')}h ${minutes}m`
}

/**
 * Formats short durations as "Xm Ys" (e.g. 95 -> "1m 35s").
 * Negative or non-finite inputs render as "0m 0s".
 */
export const formatDurationMinutes = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0
  const minutes = Math.floor(safeSeconds / 60)
  return `${minutes}m ${safeSeconds % 60}s`
}
